import { useCallback, useEffect, useMemo, useRef } from "react";
import { Download, FolderInput, FolderLock, Loader2, Mail, MailOpen, Menu, Paperclip, Trash2, X } from "lucide-react";
import { InboxListFilterSelect } from "@/features/inbox/InboxListFilterSelect";
import {
  isBodyCacheOpenable,
  type MessageBodyCacheStatus,
} from "@/features/inbox/MessageBodyCacheBadge";
import { MailActivityJournalButton } from "@/features/shell/MailActivityJournalButton";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { MobileInboxSearchBar } from "@/mobile/components/inbox/MobileInboxSearchBar";
import { MobileHeader } from "@/mobile/components/ui/MobileHeader";
import { MobileButton } from "@/mobile/components/ui/MobileButton";
import { MobileScreen } from "@/mobile/components/ui/MobileScreen";
import { MobileEmpty } from "@/mobile/components/ui/MobileEmpty";
import { MobilePullToRefresh } from "@/mobile/components/ui/MobilePullToRefresh";
import { inboxScrollMemoryKey } from "@/mobile/lib/mobileInboxScrollMemory";
import { MobileSwipeableMessageRow } from "@/mobile/components/message/MobileSwipeableMessageRow";
import { MobileMessageActionSheet } from "@/mobile/components/message/MobileMessageActionSheet";
import { useInboxBodyCacheFlags } from "@/hooks/useInboxBodyCacheFlags";
import { useInboxPreparingMessages } from "@/hooks/useInboxPreparingMessages";
import { queueWarmMessageBodies } from "@/lib/mail/bodyWarmup";
import {
  isClientHydrateSettled,
  schedulePrimeVisibleReadyBodies,
} from "@/lib/mail/primeVisibleReadyBodies";
import { warmMessageBodyForOpen } from "@/lib/mail/bodyCacheLookup";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import { INBOX_CLIENT_HYDRATE_MAX } from "@/lib/constants/inbox";
import type { useInboxBulkActions } from "@/hooks/useInboxBulkActions";
import type { useMobileActions } from "@/mobile/hooks/useMobileActions";
import type { InboxListFilter } from "@/lib/mail/messageLabels";
import type { MailAccountSummary, MailMessageSummary } from "@/lib/types/mail";

type Props = {
  account: MailAccountSummary | null;
  accountId: number | null;
  folder: string;
  folderTitle: string;
  messages: MailMessageSummary[];
  loadingInitial: boolean;
  syncing: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  listFilter: InboxListFilter;
  onListFilterChange: (next: InboxListFilter) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  searchDisabled?: boolean;
  searchEmpty?: boolean;
  searchImapLoading?: boolean;
  searchDebouncePending?: boolean;
  isDraftsView?: boolean;
  isSentView?: boolean;
  isOutboxView?: boolean;
  /** Bloque le scroll liste sous le lecteur plein écran. */
  scrollLocked?: boolean;
  onOpenNav: () => void;
  onRefresh: () => void;
  onSelectMessage: (uid: number, event?: React.MouseEvent) => void;
  onPrefetchMessage?: (msg: MailMessageSummary) => void;
  bulk: ReturnType<typeof useInboxBulkActions>;
  actions: ReturnType<typeof useMobileActions>;
};

export function MobileInboxScreen({
  account,
  accountId,
  folder,
  folderTitle,
  messages,
  loadingInitial,
  syncing,
  refreshing,
  error,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  listFilter,
  onListFilterChange,
  searchInput,
  onSearchInputChange,
  searchDisabled = false,
  searchEmpty = false,
  searchImapLoading = false,
  searchDebouncePending = false,
  isDraftsView = false,
  isSentView = false,
  isOutboxView = false,
  scrollLocked = false,
  onOpenNav,
  onRefresh,
  onSelectMessage,
  onPrefetchMessage,
  bulk,
  actions,
}: Props) {
  const bulkCount = bulk.selectionCount;
  const bulkActive = bulkCount > 0;
  const showCheckboxes = !isOutboxView;
  const headerActionBtn =
    "rounded-full p-2 text-[var(--mail-muted)] transition active:bg-[var(--mail-surface-hover)] active:text-[var(--mail-text)]";
  const bulkActionBtn =
    "text-[var(--mail-muted)] transition active:bg-[var(--mail-surface-hover)] active:text-[var(--mail-text)]";
  const searchAnchorRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const listUids = useMemo(
    () => messages.filter((m) => m.uid > 0).map((m) => m.uid),
    [messages]
  );

  const listBodyCached = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const msg of messages) {
      if (msg.uid > 0 && msg.bodyCached) {
        map.set(msg.uid, true);
      }
    }
    return map;
  }, [messages]);

  const priorityBodyUids = useMemo(
    () => messages.filter((m) => m.uid > 0 && !m.seen).map((m) => m.uid),
    [messages]
  );

  const bodyCacheFlags = useInboxBodyCacheFlags(
    accountId ?? undefined,
    folder,
    listUids,
    listBodyCached,
    priorityBodyUids
  );

  const searchImapPending = searchInput.trim().length > 0 && searchImapLoading;
  const initialLoading =
    (syncing || loadingInitial) &&
    messages.length === 0 &&
    !searchImapPending &&
    !searchDebouncePending;
  /** Aligné desktop : pas d’hydrate / preparing sur drafts, Sent, outbox. */
  const preparingEnabled = !isDraftsView && !isSentView && !isOutboxView;
  const listSettled =
    preparingEnabled &&
    (messages.length > 0 || (!loadingInitial && !syncing && !initialLoading));

  const isClientBodyReady = useCallback(
    (msg: MailMessageSummary) => {
      if (msg.uid <= 0) return false;
      if (Boolean(msg.bodyCached)) return true;
      if (accountId == null) return false;
      return isMessageBodyCached(accountId, folder, msg.uid);
    },
    [accountId, folder]
  );

  const primeArrivalBody = useCallback(
    (msg: MailMessageSummary) => {
      if (accountId == null || msg.uid <= 0) return;
      queueWarmMessageBodies(accountId, folder, [msg.uid], { newMail: true });
    },
    [accountId, folder]
  );

  const hydrateRevealBody = useCallback(
    (msg: MailMessageSummary) => {
      if (accountId == null || msg.uid <= 0) return;
      void warmMessageBodyForOpen(accountId, folder, msg.uid, msg);
    },
    [accountId, folder]
  );

  const clientReadyRevision = useMemo(() => {
    let localReady = 0;
    let warming = 0;
    for (const msg of messages) {
      if (msg.uid <= 0) continue;
      if (
        Boolean(msg.bodyCached) ||
        (accountId != null && isMessageBodyCached(accountId, folder, msg.uid))
      ) {
        localReady += 1;
      }
      const status = bodyCacheFlags.get(msg.uid) ?? "idle";
      if (status === "warming") warming += 1;
    }
    return `${localReady}:${warming}:${bodyCacheFlags.size}`;
  }, [accountId, bodyCacheFlags, folder, messages]);

  const { visibleMessages } = useInboxPreparingMessages(messages, {
    enabled: preparingEnabled,
    scopeKey: `${accountId ?? "none"}:${folder}`,
    listSettled,
    accountId: accountId ?? undefined,
    folder,
    isClientReady: isClientBodyReady,
    clientReadyRevision,
    onPrimeArrival: primeArrivalBody,
    onRevealReady: hydrateRevealBody,
  });

  const hydrateFingerprint = useMemo(() => {
    const hydrateListReady =
      !isOutboxView &&
      accountId != null &&
      messages.length > 0 &&
      (listSettled || isSentView || isDraftsView);
    if (!hydrateListReady || accountId == null) {
      return "";
    }
    const parts: string[] = [];
    for (const msg of messages) {
      if (msg.uid <= 0) continue;
      const status = bodyCacheFlags.get(msg.uid) ?? "idle";
      if (msg.bodyCached !== true && status !== "ready") continue;
      if (isClientHydrateSettled(accountId, folder, msg.uid)) continue;
      parts.push(`${accountId}:${folder}:${msg.uid}`);
      if (parts.length >= INBOX_CLIENT_HYDRATE_MAX) break;
    }
    return parts.join("|");
  }, [
    accountId,
    bodyCacheFlags,
    clientReadyRevision,
    folder,
    isDraftsView,
    isOutboxView,
    isSentView,
    listSettled,
    messages,
  ]);

  useEffect(() => {
    if (!hydrateFingerprint || accountId == null) return;
    const entries = messages
      .filter((msg) => {
        if (msg.uid <= 0) return false;
        const status = bodyCacheFlags.get(msg.uid) ?? "idle";
        if (msg.bodyCached !== true && status !== "ready") return false;
        return !isClientHydrateSettled(accountId, folder, msg.uid);
      })
      .slice(0, INBOX_CLIENT_HYDRATE_MAX)
      .map((msg) => ({
        accountId,
        folder,
        uid: msg.uid,
        priority: msg.seen === false,
      }));
    if (entries.length === 0) return;
    schedulePrimeVisibleReadyBodies(
      entries,
      INBOX_CLIENT_HYDRATE_MAX,
      `${accountId}:${folder}`
    );
  }, [accountId, bodyCacheFlags, folder, hydrateFingerprint, messages]);

  const emptyHint = searchInput.trim()
    ? searchEmpty && !searchDebouncePending && !searchImapPending
      ? "Aucun message ne correspond à cette recherche."
      : searchDebouncePending
        ? "Recherche en cours…"
        : searchImapPending
          ? "Recherche IMAP en cours…"
          : "Recherche en cours…"
    : "Ce dossier est vide pour le moment.";

  return (
    <MobileScreen className="mobile-screen--inbox">
      <MobileHeader
        title={folderTitle}
        subtitle={account?.email}
        left={
          <MobileButton variant="icon" onClick={onOpenNav} aria-label="Comptes et dossiers">
            <Menu className="h-5 w-5" aria-hidden />
          </MobileButton>
        }
        right={
          bulkActive ? (
            <>
              <span className="shrink-0 text-[11px] text-[var(--mail-muted)] tabular-nums">
                {bulkCount} sél.
              </span>
              <button
                type="button"
                onClick={() => bulk.clearSelection()}
                className={headerActionBtn}
                aria-label="Tout désélectionner"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <MailActivityJournalButton presentation="sheet" />
              <InboxListFilterSelect
                filter={listFilter}
                disabled={searchDisabled}
                onChange={onListFilterChange}
              />
            </>
          )
        }
      />

      {bulkActive ? (
        <div
          ref={searchAnchorRef}
          className="mobile-bulk-bar"
          role="toolbar"
          aria-label="Actions sur la sélection"
        >
          <div className="mobile-bulk-bar__scroll">
            <button
              type="button"
              onClick={() => void bulk.handleBulkAction("markRead")}
              className={bulkActionBtn}
              aria-label="Marquer comme lu"
            >
              <MailOpen className="h-4 w-4" aria-hidden />
              <span className="mobile-bulk-bar__label">Lu</span>
            </button>
            <button
              type="button"
              onClick={() => void bulk.handleBulkAction("markUnread")}
              className={bulkActionBtn}
              aria-label="Marquer comme non lu"
            >
              <Mail className="h-4 w-4" aria-hidden />
              <span className="mobile-bulk-bar__label">Non lu</span>
            </button>
            <button
              type="button"
              onClick={() => void bulk.handleBulkAction("move")}
              className={bulkActionBtn}
              aria-label="Déplacer"
            >
              <FolderInput className="h-4 w-4" aria-hidden />
              <span className="mobile-bulk-bar__label">Déplacer</span>
            </button>
            <button
              type="button"
              onClick={() => void bulk.handleBulkAction("delete")}
              className={`${bulkActionBtn} text-[var(--mail-danger-text)]`}
              aria-label="Supprimer"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              <span className="mobile-bulk-bar__label">Suppr.</span>
            </button>
            {!isOutboxView ? (
              <>
                <button
                  type="button"
                  onClick={() => void bulk.handleBulkAction("selectWithAttachments")}
                  className={bulkActionBtn}
                  aria-label="Messages avec PJ"
                >
                  <Paperclip className="h-4 w-4" aria-hidden />
                  <span className="mobile-bulk-bar__label">Avec PJ</span>
                </button>
                <button
                  type="button"
                  onClick={() => void bulk.handleBulkAction("downloadAttachments")}
                  className={bulkActionBtn}
                  aria-label="Télécharger PJ"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  <span className="mobile-bulk-bar__label">Téléch.</span>
                </button>
                <button
                  type="button"
                  onClick={() => void bulk.handleBulkAction("saveToCoffre")}
                  className={bulkActionBtn}
                  aria-label="Coffre"
                >
                  <FolderLock className="h-4 w-4" aria-hidden />
                  <span className="mobile-bulk-bar__label">Coffre</span>
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : (
        <div ref={searchAnchorRef}>
          <MobileInboxSearchBar
            value={searchInput}
            onChange={onSearchInputChange}
            disabled={searchDisabled}
            busy={searchImapPending}
          />
        </div>
      )}

      {error ? (
        <p className="mx-4 mt-2 mail-alert-error px-3 py-2 text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <MobilePullToRefresh
        onRefresh={onRefresh}
        refreshing={refreshing}
        scrollMemoryKey={inboxScrollMemoryKey(accountId, folder)}
        scrollRestoreDep={messages.length}
        scrollLocked={scrollLocked}
        scrollContainerRef={scrollContainerRef}
      >
        {searchImapPending && messages.length === 0 ? (
          <MailLoadingState variant="search" layout="compact" />
        ) : null}

        {searchImapPending && messages.length > 0 ? (
          <div
            className="mx-3 mb-1 overflow-hidden rounded-lg border border-[var(--mail-accent)]/20 bg-[var(--mail-accent-soft)] px-3 py-2"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center justify-center gap-2 text-xs font-medium text-[var(--mail-accent)]">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
              <span>Recherche IMAP en cours dans toute la boîte…</span>
            </div>
            <div className="mt-1.5 h-0.5 overflow-hidden rounded-full bg-[var(--mail-border-soft)]">
              <div className="inbox-load-progress-indeterminate h-full w-2/5 rounded-full bg-[var(--mail-accent)]" />
            </div>
          </div>
        ) : null}

        {initialLoading ? (
          <MailLoadingState variant="messages" layout="compact" />
        ) : null}

        {!initialLoading && !searchImapPending && messages.length === 0 ? (
          <MobileEmpty title="Aucun message" hint={emptyHint} />
        ) : null}

        {messages.length > 0 ? (
          <ul className="m-0 list-none p-0">
            {visibleMessages.map((msg) => {
              const bodyStatus: MessageBodyCacheStatus =
                bodyCacheFlags.get(msg.uid) ?? "idle";
              const showBadge =
                !isOutboxView &&
                msg.uid > 0 &&
                msg.sendStatus == null &&
                bodyStatus !== "warming";
              const bodyOpenable =
                isDraftsView || isSentView || isOutboxView || isBodyCacheOpenable(bodyStatus);
              return (
                <li
                  key={msg.uid <= 0 ? `outbox-${msg.outboxId ?? msg.uid}` : msg.uid}
                  data-mail-uid={msg.uid > 0 ? msg.uid : undefined}
                >
                  <MobileSwipeableMessageRow
                    subject={msg.subject}
                    from={msg.from}
                    to={msg.to}
                    preferRecipient={isSentView || isDraftsView}
                    date={msg.date}
                    seen={msg.seen}
                    hasAttachments={msg.hasAttachments}
                    showBodyCacheBadge={showBadge}
                    bodyCacheStatus={bodyStatus}
                    bodyOpenable={bodyOpenable}
                    checked={bulk.checkedUids.has(msg.uid)}
                    showCheckbox={showCheckboxes && msg.sendStatus == null}
                    onPrefetch={() => {
                      if (bodyOpenable) onPrefetchMessage?.(msg);
                    }}
                    onSelect={() => {
                      if (!bodyOpenable) return;
                      onSelectMessage(msg.uid);
                    }}
                    onToggleCheck={() => bulk.handleToggleCheck(msg.uid)}
                    onMarkUnread={() => void actions.markUnread(msg.uid, msg.seen)}
                    onDelete={() => void actions.deleteMessage(msg.uid, msg.seen)}
                    onLongPress={() => actions.openActions(msg)}
                    pendingExit={bulk.exitingUids.has(msg.uid)}
                  />
                </li>
              );
            })}
            {hasMore && onLoadMore ? (
              <li className="px-4 py-3 pb-2">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="w-full rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2.5 text-xs font-medium text-[var(--mail-accent)] shadow-[var(--mail-shadow-sm)] transition active:opacity-80 disabled:opacity-50"
                >
                  {loadingMore ? "Chargement…" : "Afficher les messages plus anciens"}
                </button>
              </li>
            ) : null}
            <li className="mobile-inbox-fab-spacer" aria-hidden />
          </ul>
        ) : null}
      </MobilePullToRefresh>

      <MobileMessageActionSheet
        open={actions.actionMessage != null}
        subject={actions.actionMessage?.subject}
        items={actions.sheetItems}
        onAction={(id) => void actions.handleSheetAction(id)}
        onClose={actions.closeActions}
      />
    </MobileScreen>
  );
}
